/**
 * AuthorMapView — LIM Level 1: SVG mind-map graph with drill-in.
 *
 * Click an argument → the whole graph rotates so the selected node
 * lands at 3 o'clock (right side, or bottom on phones). Then P-step boxes
 * appear branching out from that node, using all available space.
 * Click a concept → emit a navigate event (caller decides).
 * Everything happens inside the same SVG scene — no page navigation.
 *
 * F3 restyle-mappe: vocabolario di PROFONDITÀ 3D (golden) portato in filosofia
 *   - Orbe-medaglione centrale = filosofo (cupola chiara, anello luce/ombra,
 *     arco di luce, ombra portata; il ritratto è INCASTONATO con volume e il
 *     nome/date restano in testo scuro leggibile sul plateau chiaro dell'orbe).
 *   - Tessere in RILIEVO = satelliti (Argomento=ametista, Concetto=teal):
 *     gradiente verticale per categoria + bordo categoria + filo di luce in
 *     alto + ombra morbida.
 *   - Connettori MATERICI = raggi tinti per la categoria del nodo di arrivo
 *     (alone tenue + tratto pieno), non fili grigi piatti.
 *   - viewBox ASPECT-CONTINUO (dimsForAspect, clamp ~9:16..16:9).
 *
 * Espone inoltre `MapSelectionApi` su `.author-map-view.__lbMapSelection`
 * (cattura "a pezzi" del lesson-builder): il service la ritrova via DOM.
 */

import { getDataService } from '@/services/data-registry';
import type {
  PhilosopherLocalized,
  ConceptLocalized,
  ArgomentoLocalized,
  TappaLocalized,
} from '@/data/schema';
import { CATEGORY_COLORS } from '@/data/category-colors';

/* ── Types ── */
interface GNode {
  id: string;
  label: string;
  sublabel?: string;
  imageId?: string;
  type: 'central' | 'argument' | 'concept' | 'school' | 'opera' | 'relation-group';
  x: number;
  y: number;
  w: number;
  h: number;
  angle?: number; // radians from center, stored for rotation
  action?: { kind: string; id: string };
  groupItems?: { label: string; action?: { kind: string; id: string } }[];
}

interface GEdge {
  from: string;
  to: string;
  dashed?: boolean;
}

/* Palette CHIARA per categoria (F2): ogni tipo la SUA tinta canonica
   (category-colors.ts). L'iris del brand (`philosopher`) resta RISERVATO al
   nodo centrale / selezione — mai come categoria generica. */
const COLORS: Record<string, { bg: string; border: string; text: string; glow: string }> = {
  central:          { bg: 'rgba(88,80,200,0.10)',  border: CATEGORY_COLORS.philosopher, text: '#1b1c2b', glow: 'rgba(88,80,200,0.18)' },
  argument:         { bg: 'rgba(138,90,166,0.10)', border: CATEGORY_COLORS.argument,    text: '#1b1c2b', glow: 'rgba(138,90,166,0.16)' },
  concept:          { bg: 'rgba(29,125,140,0.10)', border: CATEGORY_COLORS.concept,     text: '#1b1c2b', glow: 'rgba(29,125,140,0.16)' },
  school:           { bg: 'rgba(84,103,143,0.08)', border: CATEGORY_COLORS.school,      text: '#4a4c63', glow: 'rgba(84,103,143,0.10)' },
  opera:            { bg: 'rgba(84,103,143,0.06)', border: 'rgba(84,103,143,0.40)',     text: '#4a4c63', glow: 'rgba(84,103,143,0.08)' },
  'relation-group': { bg: 'rgba(88,80,200,0.08)',  border: CATEGORY_COLORS.philosopher, text: '#1b1c2b', glow: 'rgba(88,80,200,0.14)' },
};

/* ── Profondità: tinte chiare/scure per dare volume (carta + orbe + ombre).
   Stessa logica delle sfere-autore: la mappa non deve apparire "piatta". ── */
function _rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** schiarisce verso il bianco (t=0 colore pieno, t=1 bianco). */
function tintW(hex: string, t: number): string {
  const [r, g, b] = _rgb(hex); const m = (c: number) => Math.round(c + (255 - c) * t);
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}
/** scurisce verso il nero. */
function shadeB(hex: string, t: number): string {
  const [r, g, b] = _rgb(hex); const m = (c: number) => Math.round(c * (1 - t));
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}

/** <defs> condivisi: gradienti-carta per categoria, orbe centrale, ombre morbide,
 *  gradienti delle tessere-step. Inseriti DENTRO l'<svg> così il clone di
 *  cattura li conserva. Ombre FREDDE (non i bruni del golden): palette chiara. */
function mapDefs(U: number): string {
  const tileKeys: Array<keyof typeof COLORS> = ['argument', 'concept', 'school', 'opera', 'relation-group'];
  const cardGrads = tileKeys.map((k) => `<linearGradient id="amv-card-${k}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${tintW(COLORS[k].border, 0.93)}"/>
        <stop offset="1" stop-color="${tintW(COLORS[k].border, 0.80)}"/>
      </linearGradient>`).join('');
  const iris = CATEGORY_COLORS.philosopher;
  const orb = `<radialGradient id="amv-orb" cx="38%" cy="30%" r="80%">
      <stop offset="0" stop-color="#ffffff"/>
      <stop offset="42%" stop-color="${tintW(iris, 0.90)}"/>
      <stop offset="100%" stop-color="${tintW(iris, 0.66)}"/>
    </radialGradient>
    <linearGradient id="amv-ring" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="${tintW(iris, 0.24)}"/>
      <stop offset="1" stop-color="${shadeB(iris, 0.22)}"/>
    </linearGradient>`;
  const stepGrads = `<linearGradient id="amv-step-prem" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="${tintW(COLORS.argument.border, 0.94)}"/>
      <stop offset="1" stop-color="${tintW(COLORS.argument.border, 0.85)}"/>
    </linearGradient>
    <linearGradient id="amv-step-concl" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="${tintW(COLORS.concept.border, 0.94)}"/>
      <stop offset="1" stop-color="${tintW(COLORS.concept.border, 0.85)}"/>
    </linearGradient>`;
  const filt = `<filter id="amv-shadow" x="-40%" y="-40%" width="180%" height="180%">
      <feDropShadow dx="0" dy="${(U * 0.12).toFixed(1)}" stdDeviation="${(U * 0.2).toFixed(1)}" flood-color="#1b1c2b" flood-opacity="0.18"/>
    </filter>
    <filter id="amv-orb-shadow" x="-70%" y="-70%" width="240%" height="240%">
      <feDropShadow dx="0" dy="${(U * 0.2).toFixed(1)}" stdDeviation="${(U * 0.5).toFixed(1)}" flood-color="#1b1c2b" flood-opacity="0.20"/>
    </filter>`;
  return `<defs>${cardGrads}${orb}${stepGrads}${filt}</defs>`;
}

/* SVG viewport — aspect CONTINUO: il viewBox segue le proporzioni del
   contenitore (clampate tra 9:16 e 16:9, area logica costante) → la mappa
   riempie telefono, tablet e PC senza bande vuote né effetto "schiacciato". */
type MapDims = { VW: number; VH: number; CX: number; CY: number };
const BASE_AREA = 1600 * 900;
function dimsForAspect(aspect: number): MapDims {
  const a = Math.min(16 / 9, Math.max(9 / 16, aspect || 16 / 9));
  const VW = Math.round(Math.sqrt(BASE_AREA * a));
  const VH = Math.round(VW / a);
  return { VW, VH, CX: VW / 2, CY: VH / 2 };
}

/* ── Helpers ── */
function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function wrapLines(text: string, maxChars: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (line.length + word.length + 1 > maxChars && line) {
      lines.push(line);
      if (lines.length >= maxLines) return lines;
      line = word;
    } else {
      line += (line ? ' ' : '') + word;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  return lines;
}

function shortName(fullName: string): string {
  const parts = fullName.split(/\s+/);
  if (parts.length === 1) return fullName;
  if (fullName.includes(' di ') || fullName.includes(" d'")) return parts[0];
  return parts[parts.length - 1];
}

function cleanTermMarkers(s: string): string {
  return s.replace(/\{\{[^|]*\|([^}]*)\}\}/g, '$1');
}

function shortSummary(text: string): string {
  const clean = cleanTermMarkers(text);
  const match = clean.match(/^(.{20,60}?)[,;:—–\-]/) || clean.match(/^(.{20,80}?)\.\s/);
  if (match) return match[1].trim();
  if (clean.length <= 65) return clean;
  return clean.slice(0, 60).replace(/\s+\S*$/, '') + '…';
}

/* ── Proportional sizes ── */
const SIZE_TABLE: Record<string, { wU: number; hU: number }> = {
  central:          { wU: 10.0, hU: 10.0 },
  argument:         { wU: 13.0, hU: 5.2 },
  concept:          { wU: 11.5, hU: 4.6 },
  school:           { wU: 10.0, hU: 3.8 },
  opera:            { wU: 12.0, hU: 4.1 },
  'relation-group': { wU: 12.0, hU: 4.1 },
};

const FONT_TABLE: Record<string, { label: number; sublabel: number; action: number }> = {
  central:          { label: 2.0,  sublabel: 1.0,  action: 0 },
  argument:         { label: 1.2,  sublabel: 0,    action: 0.88 },
  concept:          { label: 1.2,  sublabel: 0,    action: 0.88 },
  school:           { label: 1.15, sublabel: 0,    action: 0 },
  opera:            { label: 1.15, sublabel: 0,    action: 0.82 },
  'relation-group': { label: 1.15, sublabel: 0,    action: 0.82 },
};

/* ── Layout engine ── */
function buildGraph(
  author: PhilosopherLocalized,
  concepts: ConceptLocalized[],
  argomenti: ArgomentoLocalized[],
  dims: MapDims,
): { nodes: GNode[]; edges: GEdge[]; U: number } {
  const { VW, VH, CX, CY } = dims;
  const nodes: GNode[] = [];
  const edges: GEdge[] = [];

  type Sat = Omit<GNode, 'x' | 'y' | 'w' | 'h'> & { sizeKey: string; priority: number };
  const sats: Sat[] = [];

  for (const a of argomenti) {
    sats.push({
      id: `arg-${a.id}`, label: a.nome, type: 'argument', sizeKey: 'argument',
      priority: 1, action: { kind: 'argument', id: a.id },
    });
  }
  for (const c of concepts) {
    sats.push({
      id: `con-${c.id}`, label: c.nome, type: 'concept', sizeKey: 'concept',
      priority: 2, action: { kind: 'concept', id: c.id },
    });
  }

  sats.sort((a, b) => a.priority - b.priority);
  const N = sats.length;
  const U = N <= 4 ? 32 : N <= 7 ? 30 : N <= 10 ? 27 : 24;

  const cSize = SIZE_TABLE.central;
  nodes.push({
    id: 'center', label: shortName(author.nome),
    sublabel: `${Math.abs(author.anno_nascita)} ${author.anno_nascita < 0 ? 'a.C.' : ''} – ${Math.abs(author.anno_morte)} ${author.anno_morte < 0 ? 'a.C.' : ''}`,
    imageId: author.id,
    type: 'central', x: CX, y: CY, angle: 0,
    w: cSize.wU * U, h: cSize.hU * U,
  });

  const satSizes = sats.map((s) => {
    const sz = SIZE_TABLE[s.sizeKey] ?? SIZE_TABLE.school;
    return { w: sz.wU * U, h: sz.hU * U };
  });
  const maxSatW = Math.max(...satSizes.map((s) => s.w), 0);
  const maxSatH = Math.max(...satSizes.map((s) => s.h), 0);

  const centralRx = cSize.wU * U / 2;
  const centralRy = cSize.hU * U / 2;
  const outerRx = CX - maxSatW / 2 - U * 1.4;
  const outerRy = CY - maxSatH / 2 - U * 1.4;
  const innerRx = Math.max(outerRx * 0.58, centralRx + maxSatW / 2 + U * 1.5);
  const innerRy = Math.max(outerRy * 0.58, centralRy + maxSatH / 2 + U * 1.5);
  const useSingleOrbit = N <= 4;

  const startAngle = -Math.PI / 2;
  for (let i = 0; i < N; i++) {
    const s = sats[i];
    const angle = startAngle + (2 * Math.PI * i) / N;

    let rx: number, ry: number;
    if (useSingleOrbit) {
      rx = (innerRx + outerRx) / 2;
      ry = (innerRy + outerRy) / 2;
    } else {
      const isOuter = i % 2 === 1;
      rx = isOuter ? outerRx : innerRx;
      ry = isOuter ? outerRy : innerRy;
    }

    const x = CX + rx * Math.cos(angle);
    const y = CY + ry * Math.sin(angle);
    const sz = satSizes[i];

    nodes.push({ ...s, x, y, w: sz.w, h: sz.h, angle, sizeKey: undefined } as unknown as GNode);
    edges.push({ from: 'center', to: s.id, dashed: false });
  }

  // Collision resolver
  const gap = U * 0.6;
  const edge = U * 1.4;
  for (let iter = 0; iter < 80; iter++) {
    let moved = false;
    for (let i = 1; i < nodes.length; i++) {
      const a = nodes[i];
      for (let j = 0; j < nodes.length; j++) {
        if (i === j) continue;
        const b = nodes[j];
        const overlapX = (a.w / 2 + b.w / 2 + gap) - Math.abs(a.x - b.x);
        const overlapY = (a.h / 2 + b.h / 2 + gap) - Math.abs(a.y - b.y);
        if (overlapX <= 0 || overlapY <= 0) continue;
        const pushAxis = overlapX < overlapY ? 'x' : 'y';
        const pushAmount = (pushAxis === 'x' ? overlapX : overlapY) * 0.5;
        if (j === 0) {
          const dx = a.x - CX || 0.1; const dy = a.y - CY || 0.1;
          if (pushAxis === 'x') a.x += Math.sign(dx) * pushAmount;
          else a.y += Math.sign(dy) * pushAmount;
        } else {
          const dx = a.x - b.x || 0.1; const dy = a.y - b.y || 0.1;
          const half = pushAmount * 0.5;
          if (pushAxis === 'x') { a.x += Math.sign(dx) * half; b.x -= Math.sign(dx) * half; }
          else { a.y += Math.sign(dy) * half; b.y -= Math.sign(dy) * half; }
        }
        moved = true;
      }
      a.x = Math.max(a.w / 2 + edge, Math.min(VW - a.w / 2 - edge, a.x));
      a.y = Math.max(a.h / 2 + edge, Math.min(VH - a.h / 2 - edge, a.y));
    }
    if (!moved) break;
  }

  // Recompute actual angles after collision resolver
  for (let i = 1; i < nodes.length; i++) {
    nodes[i].angle = Math.atan2(nodes[i].y - CY, nodes[i].x - CX);
  }

  return { nodes, edges, U };
}

/* ── SVG Renderer ── */
function renderGraph(nodes: GNode[], edges: GEdge[], U: number, dims: MapDims): string {
  const { VW, VH, CX, CY } = dims;
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  let edgeSvg = '';
  for (const e of edges) {
    const from = nodeMap.get(e.from)!;
    const to = nodeMap.get(e.to)!;
    const dx = to.x - from.x, dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dx / dist, ny = dy / dist;
    // Distanza dal centro al BORDO del nodo lungo (nx,ny): cerchio per il
    // centrale, rettangolo per i box → la linea si ferma sul bordo, non entra.
    const edgeR = (node: GNode): number => {
      if (node.type === 'central') return Math.min(node.w, node.h) / 2;
      const ax = Math.abs(nx) || 1e-6, ay = Math.abs(ny) || 1e-6;
      return Math.min((node.w / 2) / ax, (node.h / 2) / ay);
    };
    const x1 = from.x + nx * edgeR(from), y1 = from.y + ny * edgeR(from);
    const x2 = to.x - nx * edgeR(to), y2 = to.y - ny * edgeR(to);
    // Connettore MATERICO: raggio tinto per la categoria del nodo di arrivo
    // (alone tenue + tratto pieno) → "aggancia" il nodo, non filo grigio piatto.
    const ec = (COLORS[to.type] ?? COLORS.concept).border;
    const sw = U * 0.07;
    const attrs = `data-edge-from="${e.from}" data-edge-to="${e.to}"`;
    edgeSvg += `<line ${attrs} x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${ec}" stroke-opacity="0.14" stroke-width="${sw * 2.4}" stroke-linecap="round" />`
      + `<line ${attrs} x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${ec}" stroke-opacity="0.45" stroke-width="${sw}" stroke-linecap="round" />`;
  }

  let nodeSvg = '';
  for (const n of nodes) {
    const c = COLORS[n.type] ?? COLORS.school;
    const isCircle = n.type === 'central';
    const isClickable = n.action || n.groupItems;
    const dataAttrs = [
      `data-node-id="${n.id}"`,
      `data-node-type="${n.type}"`,
      n.action ? `data-action="${n.action.kind}" data-action-id="${n.action.id}"` : '',
    ].filter(Boolean).join(' ');
    const cursor = isClickable ? 'pointer' : 'default';
    const fonts = FONT_TABLE[n.type] ?? FONT_TABLE.school;

    if (isCircle) {
      // ── ORBE-MEDAGLIONE: cupola chiara + anello luce/ombra + arco di luce +
      //    ombra portata. Il ritratto è INCASTONATO nella metà superiore come
      //    medaglione; il nome/date stanno sul plateau chiaro inferiore in
      //    testo scuro leggibile. ──
      const r = n.w / 2;
      const ringW = U * 0.11;
      const hasPortrait = Boolean(n.imageId);
      const imgHref = n.imageId ? `/images/authors/${n.imageId}.webp` : '';

      // Arco di luce speculare sulla calotta alta dell'orbe.
      const specular = `<path d="M ${n.x - r * 0.56} ${n.y - r * 0.7} A ${r * 0.88} ${r * 0.88} 0 0 1 ${n.x + r * 0.56} ${n.y - r * 0.7}"
            fill="none" stroke="rgba(255,255,255,0.72)" stroke-width="${r * 0.05}" stroke-linecap="round" pointer-events="none" />`;
      const ring = `<circle cx="${n.x}" cy="${n.y}" r="${r - ringW / 2}" fill="none" stroke="url(#amv-ring)" stroke-width="${ringW}" />`;
      const base = `<circle cx="${n.x}" cy="${n.y}" r="${r}" fill="url(#amv-orb)" filter="url(#amv-orb-shadow)" />`;

      if (hasPortrait) {
        const rp = r * 0.54;
        const pcy = n.y - r * 0.26;
        const clipId = `clip-portrait-${n.id}`;
        const nameFs = U * 1.5;
        const subFs = U * 0.82;
        nodeSvg += `
          <g class="graph-node" ${dataAttrs} style="cursor:${cursor}">
            <defs><clipPath id="${clipId}"><circle cx="${n.x}" cy="${pcy}" r="${rp - U * 0.05}" /></clipPath></defs>
            ${base}
            <image href="${imgHref}" x="${n.x - rp}" y="${pcy - rp}" width="${rp * 2}" height="${rp * 2}" clip-path="url(#${clipId})" preserveAspectRatio="xMidYMid slice" />
            <circle cx="${n.x}" cy="${pcy}" r="${rp}" fill="none" stroke="${shadeB(c.border, 0.18)}" stroke-opacity="0.55" stroke-width="${U * 0.07}" pointer-events="none" />
            <path d="M ${n.x - rp * 0.72} ${pcy - rp * 0.5} A ${rp * 0.86} ${rp * 0.86} 0 0 1 ${n.x + rp * 0.72} ${pcy - rp * 0.5}"
                  fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="${rp * 0.06}" stroke-linecap="round" pointer-events="none" />
            ${ring}${specular}
            <text x="${n.x}" y="${n.y + r * 0.46}" text-anchor="middle" fill="${c.text}" font-size="${nameFs}" font-weight="800" font-family="Playfair Display, serif">${esc(n.label)}</text>
            ${n.sublabel ? `<text x="${n.x}" y="${n.y + r * 0.72}" text-anchor="middle" fill="${shadeB(c.border, 0.05)}" font-size="${subFs}" font-family="Source Sans 3, sans-serif" letter-spacing="0.03em">${esc(n.sublabel)}</text>` : ''}
          </g>`;
      } else {
        const labelFs = fonts.label * U;
        const subFs = fonts.sublabel * U;
        nodeSvg += `
          <g class="graph-node" ${dataAttrs} style="cursor:${cursor}">
            ${base}${ring}${specular}
            <text x="${n.x}" y="${n.y - U * 0.15}" text-anchor="middle" fill="${c.text}" font-size="${labelFs}" font-weight="800" font-family="Playfair Display, serif">${esc(n.label)}</text>
            ${n.sublabel ? `<text x="${n.x}" y="${n.y + U * 0.7}" text-anchor="middle" fill="${shadeB(c.border, 0.05)}" font-size="${subFs}" font-family="Source Sans 3, sans-serif" letter-spacing="0.03em">${esc(n.sublabel)}</text>` : ''}
          </g>`;
      }
    } else {
      // ── TESSERA IN RILIEVO: gradiente verticale per categoria + bordo
      //    categoria + filo di luce in alto + ombra morbida. ──
      const labelFs = fonts.label * U;
      const actionFs = fonts.action * U;
      const lineH = labelFs * 1.25;
      const lines = wrapLines(n.label, 22, 2);
      const categoryLabel = n.type === 'argument' ? 'Argomento ▶' : n.type === 'concept' ? 'Concetto ▶' : '';
      const hasSubtitle = categoryLabel.length > 0;
      const subtitleGap = actionFs * 0.35;
      const subtitleH = hasSubtitle ? actionFs + subtitleGap : 0;
      const totalTextH = lines.length * lineH + subtitleH;
      const blockTop = n.y - totalTextH / 2;
      const firstLineY = blockTop + labelFs * 0.35;
      const rx = U * 0.45;
      const sw = U * 0.06;
      const topY = n.y - n.h / 2;
      const gradId = COLORS[n.type] ? `amv-card-${n.type}` : 'amv-card-school';

      nodeSvg += `
        <g class="graph-node" ${dataAttrs} style="cursor:${cursor}">
          <rect x="${n.x - n.w / 2}" y="${topY}" width="${n.w}" height="${n.h}" rx="${rx}" fill="url(#${gradId})" stroke="${c.border}" stroke-width="${sw}" filter="url(#amv-shadow)" />
          <line x1="${n.x - n.w / 2 + rx}" y1="${topY + sw * 1.4}" x2="${n.x + n.w / 2 - rx}" y2="${topY + sw * 1.4}" stroke="rgba(255,255,255,0.65)" stroke-width="${U * 0.045}" stroke-linecap="round" pointer-events="none" />
          ${lines.map((line, li) =>
            `<text x="${n.x}" y="${firstLineY + li * lineH}" text-anchor="middle" fill="${c.text}" font-size="${labelFs}" font-weight="600" font-family="EB Garamond, serif">${esc(line)}</text>`
          ).join('')}
          ${hasSubtitle ? `<text x="${n.x}" y="${firstLineY + lines.length * lineH + subtitleGap}" text-anchor="middle" fill="${c.border}" font-size="${actionFs}" opacity="0.75" font-family="Source Sans 3, sans-serif">${categoryLabel}</text>` : ''}
        </g>`;
    }
  }

  return `
    <svg viewBox="0 0 ${VW} ${VH}"
         preserveAspectRatio="xMidYMid meet"
         xmlns="http://www.w3.org/2000/svg"
         class="author-map-svg"
         style="width:100%;height:100%;">
      ${mapDefs(U)}
      <g class="map-root" style="transform-origin:${CX}px ${CY}px;transition:transform 0.8s cubic-bezier(0.4,0,0.2,1)">
        <g class="edges-layer">${edgeSvg}</g>
        <g class="nodes-layer">${nodeSvg}</g>
        <g class="overlay-layer"></g>
      </g>
      <g class="steps-layer"></g>
    </svg>
  `;
}

/* ─────────────── Cattura-mappe (lesson-builder) ─────────────── */

/** Snapshot strutturato del grafo, per costruire il payload `data` del blocco 'map'. */
export interface MapGraphSnapshot {
  kind: string;
  authorId: string;
  locale: string;
  viewBox: [number, number, number, number];
  /** `note` = sintesi-da-mappa del nodo (map_blurb / enunciato): il reader del
   *  hub la mostra in tendina al click. */
  nodes: Array<{ id: string; label: string; type: string; x: number; y: number; w: number; h: number; note?: string }>;
  edges: Array<{ from: string; to: string }>;
}

/** Cattura pronta per il blocco 'map': SVG ritagliato + payload strutturato. */
export interface MapCapture {
  content_html: string;
  data: Record<string, unknown>;
}

/** API di selezione "a pezzi" multi-livello esposta al lesson-builder (docente). */
export interface MapSelectionApi {
  enterSelectMode: () => void;
  exitSelectMode: () => void;
  isSelectMode: () => boolean;
  /** Id selezionati del livello corrente (nodi, o `step:idx` nel drill-in). */
  getSelectedIds: () => string[];
  clearSelection: () => void;
  onSelectionChange: (cb: (ids: string[]) => void) => void;
  getGraph: () => MapGraphSnapshot;
  /** Costruisce la cattura del livello corrente; null se nulla è selezionato. */
  buildCapture: () => MapCapture | null;
}

/** SVG di fallback per la cattura-RAMO (mini-mappa autore→nodo→step). */
function branchFallbackSvg(
  bNodes: MapGraphSnapshot['nodes'],
  bEdges: Array<{ from: string; to: string }>,
  vb: [number, number, number, number],
): string {
  const byId = new Map(bNodes.map((n) => [n.id, n]));
  const tint = (t: string): string => (COLORS[t] ?? COLORS.concept).border;
  const lines = bEdges.map((e) => {
    const a = byId.get(e.from), b = byId.get(e.to);
    if (!a || !b) return '';
    return `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" stroke="rgba(27,28,43,0.20)" stroke-width="2.5"/>`;
  }).join('');
  const boxes = bNodes.map((n) => {
    const col = tint(n.type);
    const label = n.label.length > 34 ? `${n.label.slice(0, 33)}…` : n.label;
    if (n.type === 'central') {
      return `<circle cx="${n.x}" cy="${n.y}" r="${n.w / 2}" fill="#eeecfb" stroke="${col}" stroke-width="3"/>
        <text x="${n.x}" y="${n.y + 9}" text-anchor="middle" font-family="Playfair Display,serif" font-size="30" font-weight="700" fill="#1b1c2b">${esc(label)}</text>`;
    }
    return `<rect x="${n.x - n.w / 2}" y="${n.y - n.h / 2}" width="${n.w}" height="${n.h}" rx="16" fill="${tintW(col, 0.9)}" stroke="${col}" stroke-width="2.5"/>
      <text x="${n.x}" y="${n.y + 8}" text-anchor="middle" font-family="EB Garamond,serif" font-size="24" font-weight="600" fill="#1b1c2b">${esc(label)}</text>`;
  }).join('');
  return `<svg viewBox="${vb.join(' ')}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">${lines}${boxes}</svg>`;
}

/* ── Public ── */
export async function mountAuthorMap(
  container: HTMLElement,
  authorId: string,
  locale: 'it' | 'en',
): Promise<void> {
  const data = getDataService();
  const author = await data.getAuthor(authorId, locale);
  if (!author) { container.innerHTML = '<p>Autore non trovato</p>'; return; }

  const [tappe, concepts, argomenti] = await Promise.all([
    data.getTappeByAuthor(authorId, locale),
    data.listConcepts(locale),
    data.getArgomentiByAuthor(authorId, locale),
  ]);
  const conceptIds = new Set(tappe.map((t) => t.concetto_id));
  const authorConcepts = concepts.filter((c) => conceptIds.has(c.id));

  // Sintesi-da-mappa (map_blurb / enunciato) per il campo `note` delle catture.
  const conNote = new Map<string, string | undefined>();
  for (const t of tappe as TappaLocalized[]) conNote.set(t.concetto_id, t.content?.map_blurb);
  // Tappa completa + nome per il DRILL-IN del concetto ("riassunto a mo di
  // mappa": il box a riposo mostra map_blurb, il click espande al contenuto).
  const conTappa = new Map<string, TappaLocalized>();
  for (const t of tappe as TappaLocalized[]) conTappa.set(t.concetto_id, t);
  const conName = new Map<string, string>();
  for (const c of authorConcepts) conName.set(c.id, c.nome);
  const argNote = new Map<string, string | undefined>();
  for (const a of argomenti) argNote.set(a.id, a.content?.enunciato);

  // Proporzioni dal CONTENITORE (fallback: finestra): viewBox aspect-continuo.
  const cw = container.clientWidth || window.innerWidth;
  const ch = container.clientHeight || window.innerHeight;
  const portrait = ch > cw;
  const dims = dimsForAspect(cw / ch);
  const { VW, VH, CX, CY } = dims;

  const { nodes, edges, U } = buildGraph(author, authorConcepts, argomenti, dims);

  container.innerHTML = `
    <div class="author-map-container author-map-view" data-map-view>
      <div class="author-map-viewport">
        ${renderGraph(nodes, edges, U, dims)}
      </div>
    </div>
  `;

  const svg = container.querySelector<SVGSVGElement>('.author-map-svg')!;
  const nodesLayer = svg.querySelector('.nodes-layer')!;
  const stepsLayer = svg.querySelector<SVGGElement>('.steps-layer')!;
  const overlayLayer = svg.querySelector('.overlay-layer')!;
  let focusedId: string | null = null;
  // Drill-in generalizzato: 'argument' (passo-passo) o 'concept' (tappa a
  // riassunti). `drilledActionId` = id dell'entità aperta; null = niente drill.
  let drilledKind: 'argument' | 'concept' | null = null;
  let drilledActionId: string | null = null;
  let drilledNodeId: string | null = null;
  const nodeDataMap = new Map(nodes.map((n) => [n.id, n]));

  /** Sintesi-da-mappa del nodo (per il campo `note` delle catture). */
  function noteFor(n: GNode): string | undefined {
    if (n.action?.kind === 'argument') return argNote.get(n.action.id);
    if (n.action?.kind === 'concept') return conNote.get(n.action.id);
    return undefined;
  }

  // ── Selezione "a pezzi" MULTI-LIVELLO (modalità docente) ──
  let selectMode = false;
  const selectedIds = new Set<string>();   // nodi mappa selezionati
  const selectedSteps = new Set<number>(); // step del drill-in selezionati
  let onSelChange: ((ids: string[]) => void) | null = null;

  function currentSelectionIds(): string[] {
    return drilledKind
      ? Array.from(selectedSteps).sort((a, b) => a - b).map((i) => `step:${i}`)
      : Array.from(selectedIds);
  }

  function styleNodeSelection(): void {
    const anySel = selectedIds.size > 0;
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      const id = g.dataset.nodeId!;
      const sel = selectedIds.has(id);
      g.style.filter = sel ? 'drop-shadow(0 0 16px rgba(88,80,200,0.6))' : '';
      g.style.opacity = !selectMode || !anySel || sel ? '1' : '0.3';
    });
  }
  function styleStepSelection(): void {
    const anySel = selectedSteps.size > 0;
    stepsLayer.querySelectorAll<SVGGElement>('.step-box').forEach((g) => {
      const idx = Number(g.dataset.stepIdx);
      const sel = selectedSteps.has(idx);
      const rect = g.querySelector<SVGRectElement>('.step-rect');
      if (rect) rect.style.filter = sel ? 'drop-shadow(0 0 12px rgba(88,80,200,0.75))' : '';
      g.style.opacity = !selectMode || !anySel || sel ? '1' : '0.45';
    });
  }
  function clearSelectionStyles(): void {
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.filter = ''; g.style.opacity = '1';
    });
    stepsLayer.querySelectorAll<SVGGElement>('.step-box').forEach((g) => {
      const rect = g.querySelector<SVGRectElement>('.step-rect');
      if (rect) rect.style.filter = '';
      g.style.opacity = '1';
    });
  }

  /** Clona l'SVG ripulendo stili inline (selezione/animazioni) per una cattura statica. */
  function cleanCloneForCapture(): SVGSVGElement {
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.filter = ''; g.style.opacity = ''; g.style.transform = '';
    });
    clone.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.opacity = ''; l.style.transform = '';
    });
    if (!clone.getAttribute('xmlns')) clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return clone;
  }

  /** Bounding box + padding, con viewBox MINIMO (evita zoom smodato su 1 pezzo). */
  function bbox(items: Array<{ x: number; y: number; w: number; h: number }>, pad: number): [number, number, number, number] {
    const minX = Math.min(...items.map((r) => r.x));
    const minY = Math.min(...items.map((r) => r.y));
    const maxX = Math.max(...items.map((r) => r.x + r.w));
    const maxY = Math.max(...items.map((r) => r.y + r.h));
    let x = minX - pad, y = minY - pad;
    let w = (maxX - minX) + 2 * pad, h = (maxY - minY) + 2 * pad;
    const MIN_W = VW / 3, MIN_H = VH / 3;
    if (w < MIN_W) { x -= (MIN_W - w) / 2; w = MIN_W; }
    if (h < MIN_H) { y -= (MIN_H - h) / 2; h = MIN_H; }
    return [x, y, w, h];
  }

  function buildCapture(): MapCapture | null {
    // Livello drill-in → step selezionati = cattura del RAMO (mini-mappa
    // sintetica autore → argomento → premesse scelte): si VEDE la provenienza.
    if (drilledKind) {
      if (selectedSteps.size === 0) return null;
      const branch = nodes.find((n) => n.id === drilledNodeId);
      const central = nodes.find((n) => n.type === 'central');
      if (!branch) return null;
      const stepsSel = Array.from(selectedSteps).sort((a, b) => a - b).map((i) => ({
        idx: i,
        title: `${stepData[i]?.num ?? i + 1} · ${stepData[i]?.enunciato ?? ''}`.slice(0, 80),
        summary: stepData[i]?.sintesi ?? (stepData[i] ? shortSummary(stepData[i].enunciato) : ''),
      }));

      const SW = 380, SH = 120, GAP = 40;
      const colH = stepsSel.length * (SH + GAP) - GAP;
      const H = Math.max(colH + 120, 420);
      const W = 1280;
      const stepX = 1020;
      const startY = (H - colH) / 2 + SH / 2;
      const bNodes: MapGraphSnapshot['nodes'] = [
        ...(central ? [{
          id: 'center', label: central.label, type: 'central',
          x: 150, y: H / 2, w: 190, h: 190, note: noteFor(central),
        }] : []),
        {
          id: branch.id, label: branch.label, type: branch.type,
          x: 520, y: H / 2, w: 280, h: 110, note: noteFor(branch),
        },
        ...stepsSel.map((s, i) => ({
          id: `step-${s.idx}`, label: s.title, type: branch.type,
          x: stepX, y: startY + i * (SH + GAP), w: SW, h: SH, note: s.summary || undefined,
        })),
      ];
      const bEdges = [
        ...(central ? [{ from: 'center', to: branch.id }] : []),
        ...stepsSel.map((s) => ({ from: branch.id, to: `step-${s.idx}` })),
      ];
      const vb: [number, number, number, number] = [0, 0, W, H];
      return {
        content_html: `<figure class="lb-map">${branchFallbackSvg(bNodes, bEdges, vb)}</figure>`,
        data: {
          kind: 'author-map', author_id: authorId, locale, selection: 'branch',
          drill: { actionKind: drilledKind, actionId: drilledActionId, nodeId: drilledNodeId },
          steps: stepsSel.map(({ idx, title }) => ({ idx, title })),
          nodes: bNodes, edges: bEdges, viewBox: vb,
        },
      };
    }
    // Livello mappa → nodi selezionati
    if (selectedIds.size === 0) return null;
    const idSet = selectedIds;
    const selNodes = nodes.filter((n) => idSet.has(n.id));
    const selEdges = edges.filter((e) => idSet.has(e.from) && idSet.has(e.to));
    const clone = cleanCloneForCapture();
    const sl = clone.querySelector('.steps-layer');
    if (sl) sl.innerHTML = '';
    clone.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      if (!idSet.has(g.dataset.nodeId ?? '')) g.remove();
    });
    clone.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      if (!(idSet.has(l.dataset.edgeFrom ?? '') && idSet.has(l.dataset.edgeTo ?? ''))) l.remove();
    });
    const vb = bbox(selNodes.map((n) => ({ x: n.x - n.w / 2, y: n.y - n.h / 2, w: n.w, h: n.h })), 24);
    clone.setAttribute('viewBox', vb.join(' '));
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    return {
      content_html: `<figure class="lb-map">${clone.outerHTML}</figure>`,
      data: {
        kind: 'author-map', author_id: authorId, locale, selection: 'subset',
        node_ids: Array.from(idSet),
        nodes: selNodes.map((n) => ({
          id: n.id, label: n.label, type: n.type, x: n.x, y: n.y, w: n.w, h: n.h,
          ...(noteFor(n) ? { note: noteFor(n) } : {}),
        })),
        edges: selEdges, viewBox: vb,
      },
    };
  }

  svg.addEventListener('click', (ev) => {
    // Modalità selezione MULTI-LIVELLO.
    if (selectMode) {
      if (drilledKind) {
        const stepEl = (ev.target as SVGElement).closest<SVGGElement>('.step-box');
        if (stepEl) {
          const idx = Number(stepEl.dataset.stepIdx);
          if (selectedSteps.has(idx)) selectedSteps.delete(idx);
          else selectedSteps.add(idx);
          styleStepSelection();
          onSelChange?.(currentSelectionIds());
          return;
        }
        if (!(ev.target as SVGElement).closest('.graph-node')) {
          closeDrillIn();
          selectedSteps.clear();
          onSelChange?.(currentSelectionIds());
          styleNodeSelection();
        }
        return;
      }
      // Livello mappa: OGNI nodo si seleziona/deseleziona (l'approfondimento
      // si fa FUORI dalla modalità selezione).
      const node = (ev.target as SVGElement).closest<SVGGElement>('.graph-node');
      if (!node) return;
      const id = node.dataset.nodeId!;
      if (selectedIds.has(id)) selectedIds.delete(id);
      else selectedIds.add(id);
      styleNodeSelection();
      onSelChange?.(currentSelectionIds());
      return;
    }

    const target = (ev.target as SVGElement).closest<SVGGElement>('.graph-node');
    const stepEl = (ev.target as SVGElement).closest<SVGGElement>('.step-box');

    // Click on a step box → expand/collapse it
    if (stepEl) {
      toggleStep(stepEl);
      return;
    }

    if (!target) {
      if (drilledKind) closeDrillIn();
      else resetFocus();
      return;
    }

    const nodeId = target.dataset.nodeId!;

    // Argument / Concept → drill-in with rotation. Il concetto apre il suo
    // "riassunto a mo di mappa" (map_blurb + contenuto tappa) DENTRO la mappa,
    // come gli argomenti — NON naviga via (era fonte di confusione).
    const actKind = target.dataset.action as 'argument' | 'concept' | undefined;
    const actId = target.dataset.actionId;
    if ((actKind === 'argument' || actKind === 'concept') && actId) {
      if (drilledKind === actKind && drilledActionId === actId) closeDrillIn();
      else openDrillIn(actKind, actId, nodeId);
      return;
    }

    if (focusedId === nodeId) resetFocus();
    else setFocus(nodeId);
  });

  /* ── Drill-in: reposition nodes (orbital rotation) + show P steps ── */

  // In ORIZZONTALE la mappa scivola a sinistra e gli step occupano la metà
  // destra; in VERTICALE (telefono) la mappa scivola in ALTO e gli step la
  // fascia bassa a tutta larghezza.
  const MAP_SHIFT_X = portrait ? 0 : VW * 0.55;
  const MAP_SHIFT_Y = portrait ? VH * 0.55 : 0;
  const STEPS_LEFT = portrait ? U * 1 : VW * 0.48;
  const STEPS_RIGHT = VW - U * 1;
  const STEPS_TOP = portrait ? VH * 0.48 : U * 1;
  const STEPS_BOTTOM = VH - U * 1;

  async function openDrillIn(kind: 'argument' | 'concept', actId: string, nodeId: string): Promise<void> {
    drilledKind = kind;
    drilledActionId = actId;
    drilledNodeId = nodeId;
    overlayLayer.innerHTML = '';

    const selectedNode = nodeDataMap.get(nodeId);
    if (!selectedNode || selectedNode.angle === undefined) return;

    // Il nodo scelto ruota verso gli step: a DESTRA (0 rad) in orizzontale,
    // in BASSO (+90°) in verticale.
    const rotationRad = (portrait ? Math.PI / 2 : 0) - selectedNode.angle;

    // Clamp dello shift: il nodo selezionato resta INTERO nel quadro.
    const selR = Math.hypot(selectedNode.x - CX, selectedNode.y - CY);
    const shiftX = portrait ? 0
      : Math.min(MAP_SHIFT_X, CX + selR - selectedNode.w / 2 - U * 0.8);
    const shiftY = portrait
      ? Math.min(MAP_SHIFT_Y, CY + selR - selectedNode.h / 2 - U * 0.8)
      : 0;

    // Animate each node to its new rotated position
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      const nId = g.dataset.nodeId!;
      const node = nodeDataMap.get(nId);
      if (!node) return;

      const isSelected = nId === nodeId;
      const isCentral = node.type === 'central';

      let dx = 0, dy = 0;
      if (!isCentral) {
        const relX = node.x - CX;
        const relY = node.y - CY;
        const newX = relX * Math.cos(rotationRad) - relY * Math.sin(rotationRad);
        const newY = relX * Math.sin(rotationRad) + relY * Math.cos(rotationRad);
        dx = (newX + CX) - node.x;
        dy = (newY + CY) - node.y;
      }

      dx -= shiftX;
      dy -= shiftY;

      g.style.transition = 'transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.6s ease';
      g.style.transform = `translate(${dx}px, ${dy}px)`;
      g.style.opacity = isSelected ? '1' : isCentral ? '0.5' : '0.12';
      g.style.filter = isSelected ? 'drop-shadow(0 0 20px rgba(88,80,200,0.35))' : '';
    });

    // Shift and dim edges
    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.transition = 'transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.6s ease';
      l.style.transform = `translate(${-shiftX}px, ${-shiftY}px)`;
      l.style.opacity = '0.06';
    });

    // Show steps after the repositioning animation
    setTimeout(async () => {
      if (drilledKind !== kind || drilledActionId !== actId) return;
      await showSteps(kind, actId);
    }, 850);
  }

  // Store step data for expansion
  let stepData: { enunciato: string; commento: string; num: string; isConclusion: boolean; sintesi?: string }[] = [];
  let expandedStepIdx: number | null = null;

  async function showSteps(kind: 'argument' | 'concept', actId: string): Promise<void> {
    if (kind === 'concept') buildConceptStepData(actId);
    else await buildArgumentStepData(actId);
    if (stepData.length === 0) return;

    expandedStepIdx = null;
    stepsBuilt = false;
    stepsLayer.innerHTML = '';
    renderSteps();
    if (selectMode) styleStepSelection();
  }

  async function buildArgumentStepData(argId: string): Promise<void> {
    const arg = await data.getArgomento(argId, locale);
    stepData = [];
    if (!arg) return;

    const passo = arg.content.argomento_passo_passo;
    for (const p of passo.premesse) {
      stepData.push({
        num: `P${p.numero}`,
        enunciato: cleanTermMarkers(p.enunciato),
        commento: cleanTermMarkers(p.commento),
        isConclusion: false,
        sintesi: p.sintesi ? cleanTermMarkers(p.sintesi) : undefined,
      });
    }
    stepData.push({
      num: '∴',
      enunciato: cleanTermMarkers(passo.conclusione),
      commento: '',
      isConclusion: true,
      sintesi: passo.conclusione_sintesi ? cleanTermMarkers(passo.conclusione_sintesi) : undefined,
    });
  }

  /**
   * Tessere del DRILL-IN concetto — riassunto a mo di mappa della tappa
   * dell'autore su quel concetto. A riposo ogni box mostra la sintesi
   * (map_blurb per la principale); il click espande al contenuto completo.
   */
  function buildConceptStepData(conceptId: string): void {
    stepData = [];
    const t = conTappa.get(conceptId);
    if (!t) return;
    const c = t.content;
    const name = conName.get(conceptId) ?? 'Concetto';
    const spieg = cleanTermMarkers(c.spiegazione ?? '');

    stepData.push({
      num: '◆',
      enunciato: name,
      commento: spieg,
      isConclusion: false,
      sintesi: (c.map_blurb ?? '').trim() || shortSummary(spieg),
    });
    if (c.cosa_cambia && c.cosa_cambia.trim()) {
      const cc = cleanTermMarkers(c.cosa_cambia);
      stepData.push({ num: '→', enunciato: 'Cosa cambia', commento: cc, isConclusion: false, sintesi: shortSummary(cc) });
    }
    if (c.contesto_storico && c.contesto_storico.trim()) {
      const ctx = cleanTermMarkers(c.contesto_storico);
      stepData.push({ num: '◷', enunciato: 'Contesto storico', commento: ctx, isConclusion: false, sintesi: shortSummary(ctx) });
    }
    const cit = c.citazioni?.[0];
    if (cit) {
      const body = `${cit.testo_tradotto}${cit.testo_originale ? `\n\n(originale) ${cit.testo_originale}` : ''}`;
      stepData.push({ num: '★', enunciato: 'Citazione', commento: body, isConclusion: true, sintesi: shortSummary(cit.testo_tradotto) });
    }
  }

  // Build all steps once in DOM, then animate positions via CSS transform
  let stepsBuilt = false;

  function buildStepsDom(): void {
    const stepW = STEPS_RIGHT - STEPS_LEFT;
    const totalSteps = stepData.length;
    const stepGap = U * 0.25;
    // Gli step riempiono la fascia dedicata (metà destra in orizzontale,
    // fascia bassa in verticale), centrati al suo interno.
    const bandH = STEPS_BOTTOM - STEPS_TOP;
    const stepH = (bandH - (totalSteps - 1) * stepGap) / totalSteps;
    const totalHeight = totalSteps * stepH + (totalSteps - 1) * stepGap;
    const startY = STEPS_TOP + (bandH - totalHeight) / 2;
    const labelFs = U * 0.85;
    const numFs = U * 1.1;

    let html = '';
    for (let i = 0; i < totalSteps; i++) {
      const s = stepData[i];
      const sy = startY + i * (stepH + stepGap);
      // Preview: sintesi curata (≤120 char) o fallback runtime.
      const summary = s.sintesi ?? shortSummary(s.enunciato);
      // Tessera-step allineata al vocabolario RILIEVO: gradiente + bordo
      // categoria (premesse=ametista, conclusione=teal — iris riservato a
      // centro/selezione) + filo di luce in alto + ombra morbida.
      const borderColor = s.isConclusion ? COLORS.concept.border : COLORS.argument.border;
      const gradId = s.isConclusion ? 'amv-step-concl' : 'amv-step-prem';
      const numColor = s.isConclusion ? COLORS.concept.border : COLORS.argument.border;
      const fullText = s.commento
        ? s.commento.split(/\n\n+/).map((p) => `<p style="margin:0 0 ${U * 0.45}px">${esc(p.trim())}</p>`).join('')
        : `<p style="margin:0">${esc(s.enunciato)}</p>`;

      html += `
        <g class="step-box" data-step-idx="${i}"
           data-orig-x="${STEPS_LEFT}" data-orig-y="${sy}" data-orig-w="${stepW}" data-orig-h="${stepH}"
           style="cursor:pointer">
          <rect class="step-rect" x="${STEPS_LEFT}" y="${sy}" width="${stepW}" height="${stepH}" rx="${U * 0.4}"
                fill="url(#${gradId})" stroke="${borderColor}" stroke-width="1.5" filter="url(#amv-shadow)"
                style="transition: all 0.5s cubic-bezier(0.4,0,0.2,1)" />
          <line class="step-hi" x1="${STEPS_LEFT + U * 0.4}" y1="${sy + U * 0.12}" x2="${STEPS_RIGHT - U * 0.4}" y2="${sy + U * 0.12}"
                stroke="rgba(255,255,255,0.6)" stroke-width="${U * 0.04}" stroke-linecap="round" pointer-events="none" />
          <g class="step-summary" style="transition: opacity 0.3s ease">
            <text class="step-summary-num" x="${STEPS_LEFT + U * 0.9}" y="${sy + stepH / 2 + numFs * 0.18}" fill="${numColor}" font-size="${numFs}"
                  font-weight="700" font-family="EB Garamond, serif">${esc(s.num)}</text>
            <foreignObject class="step-summary-label" x="${STEPS_LEFT + U * 2.4}" y="${sy}" width="${stepW - U * 2.9}" height="${stepH}">
              <div xmlns="http://www.w3.org/1999/xhtml" style="
                width:100%;height:100%;display:flex;align-items:center;
                color:#1b1c2b;font-family:'EB Garamond',serif;font-size:${labelFs}px;
                font-weight:600;line-height:1.28;padding-right:${U * 0.3}px;
              ">${esc(summary)}</div>
            </foreignObject>
          </g>
          <foreignObject class="step-detail" x="${STEPS_LEFT}" y="${sy}" width="${stepW}" height="${stepH}"
                         style="opacity:0;pointer-events:none;transition:opacity 0.4s ease 0.2s">
            <div xmlns="http://www.w3.org/1999/xhtml" class="step-detail-inner" style="
              width:100%;height:100%;overflow-y:auto;color:#1b1c2b;
              font-family:'Source Sans 3',sans-serif;font-size:${U * 0.9}px;line-height:1.75;
              padding:${U * 0.5}px;opacity:0;transition:opacity 0.3s ease 0.25s;
            ">
              <div style="font-family:'EB Garamond',serif;font-size:${U * 1.25}px;font-weight:700;color:${numColor};margin-bottom:${U * 0.4}px;line-height:1.3;">
                ${esc(s.num)} — ${esc(s.enunciato)}
              </div>
              ${fullText}
            </div>
          </foreignObject>
        </g>`;
    }

    stepsLayer.innerHTML = html;
    stepsBuilt = true;
  }

  function renderSteps(): void {
    if (!stepsBuilt) {
      buildStepsDom();
      stepsLayer.style.opacity = '0';
      stepsLayer.style.transition = 'opacity 0.5s cubic-bezier(0.4,0,0.2,1)';
      requestAnimationFrame(() => { stepsLayer.style.opacity = '1'; });
    }
    applyStepLayout();
  }

  function applyStepLayout(): void {
    const totalSteps = stepData.length;
    const stepBoxes = stepsLayer.querySelectorAll<SVGGElement>('.step-box');

    if (expandedStepIdx !== null) {
      // Only prev/next visible as thin tabs, rest hidden
      const tabH = U * 1.1;
      const tabGap = U * 0.08;
      const expIdx = expandedStepIdx!;
      const hasPrev = expIdx > 0;
      const hasNext = expIdx < totalSteps - 1;

      const topMargin = STEPS_TOP + U * 0.3;
      const bottomMargin = U * 0.3;

      const prevY = topMargin;
      const expY = hasPrev ? prevY + tabH + tabGap : topMargin;
      const nextY = VH - bottomMargin - tabH;
      const expBottom = hasNext ? nextY - tabGap : VH - bottomMargin;
      const expH = expBottom - expY;

      // In orizzontale sborda a sinistra oltre la fascia step; in verticale
      // occupa quasi tutta la larghezza.
      const expX = portrait ? U * 0.5 : STEPS_LEFT - U * 6;
      const expW = VW - expX - U * 0.5;

      stepBoxes.forEach((g, i) => {
        const rect = g.querySelector<SVGRectElement>('.step-rect')!;
        const hi = g.querySelector<SVGLineElement>('.step-hi');
        const summaryG = g.querySelector<SVGGElement>('.step-summary')!;
        const summaryNum = g.querySelector<SVGTextElement>('.step-summary-num');
        const summaryLabel = g.querySelector<SVGForeignObjectElement>('.step-summary-label');
        const detail = g.querySelector<SVGForeignObjectElement>('.step-detail')!;
        const detailInner = detail.querySelector<HTMLElement>('.step-detail-inner');
        const isExp = i === expIdx;
        const isPrev = i === expIdx - 1;
        const isNext = i === expIdx + 1;

        if (isExp) {
          rect.setAttribute('x', String(expX));
          rect.setAttribute('y', String(expY));
          rect.setAttribute('width', String(expW));
          rect.setAttribute('height', String(expH));
          rect.setAttribute('stroke-width', '2');
          // Sfondo OPACO da espanso: copre la mappa attenuata sottostante.
          rect.style.fill = '#fbfaff';
          if (hi) hi.style.opacity = '0';

          summaryG.style.opacity = '0';
          summaryG.style.pointerEvents = 'none';

          detail.setAttribute('x', String(expX + U * 0.3));
          detail.setAttribute('y', String(expY + U * 0.3));
          detail.setAttribute('width', String(expW - U * 0.6));
          detail.setAttribute('height', String(expH - U * 0.6));
          detail.style.opacity = '1';
          detail.style.pointerEvents = 'auto';
          if (detailInner) detailInner.style.opacity = '1';

          g.style.opacity = '1';
          g.style.display = '';
        } else if (isPrev || isNext) {
          const tabY = isPrev ? prevY : nextY;
          rect.setAttribute('x', String(STEPS_LEFT));
          rect.setAttribute('y', String(tabY));
          rect.setAttribute('width', String(STEPS_RIGHT - STEPS_LEFT));
          rect.setAttribute('height', String(tabH));
          rect.setAttribute('stroke-width', '1');
          rect.style.fill = '';
          if (hi) hi.style.opacity = '0';

          summaryG.style.opacity = '1';
          summaryG.style.pointerEvents = 'auto';
          if (summaryNum) summaryNum.setAttribute('y', String(tabY + tabH / 2 + U * 0.25));
          if (summaryLabel) summaryLabel.style.display = 'none';

          detail.style.opacity = '0';
          detail.style.pointerEvents = 'none';
          if (detailInner) detailInner.style.opacity = '0';
          g.style.opacity = '0.55';
          g.style.display = '';
        } else {
          g.style.display = 'none';
        }
      });
    } else {
      // All at original positions
      stepBoxes.forEach((g) => {
        const rect = g.querySelector<SVGRectElement>('.step-rect')!;
        const hi = g.querySelector<SVGLineElement>('.step-hi');
        const summaryG = g.querySelector<SVGGElement>('.step-summary')!;
        const summaryNum = g.querySelector<SVGTextElement>('.step-summary-num');
        const summaryLabel = g.querySelector<SVGForeignObjectElement>('.step-summary-label');
        const detail = g.querySelector<SVGForeignObjectElement>('.step-detail')!;
        const detailInner = detail.querySelector<HTMLElement>('.step-detail-inner');

        const ox = g.dataset.origX!;
        const oy = g.dataset.origY!;
        const ow = g.dataset.origW!;
        const oh = g.dataset.origH!;

        rect.setAttribute('x', ox);
        rect.setAttribute('y', oy);
        rect.setAttribute('width', ow);
        rect.setAttribute('height', oh);
        rect.setAttribute('stroke-width', '1.5');
        rect.style.fill = '';
        if (hi) hi.style.opacity = '1';

        const origY = parseFloat(oy);
        const origH = parseFloat(oh);
        const numFs = U * 1.1;
        if (summaryNum) summaryNum.setAttribute('y', String(origY + origH / 2 + numFs * 0.18));
        if (summaryLabel) {
          summaryLabel.setAttribute('y', oy);
          summaryLabel.setAttribute('height', oh);
          summaryLabel.style.display = '';
        }

        summaryG.style.opacity = '1';
        summaryG.style.pointerEvents = 'auto';
        detail.style.opacity = '0';
        detail.style.pointerEvents = 'none';
        if (detailInner) detailInner.style.opacity = '0';

        g.style.opacity = '1';
        g.style.display = '';
        g.style.pointerEvents = 'auto';
      });
    }
  }

  function toggleStep(stepEl: SVGGElement): void {
    const idx = parseInt(stepEl.dataset.stepIdx ?? '-1', 10);
    if (idx < 0 || idx >= stepData.length) return;

    if (expandedStepIdx === idx) {
      expandedStepIdx = null; // collapse
    } else {
      expandedStepIdx = idx; // expand this one
    }
    renderSteps();
  }

  function closeDrillIn(): void {
    drilledKind = null;
    drilledActionId = null;
    drilledNodeId = null;

    // Animate nodes back to original positions
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.transition = 'transform 0.7s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease';
      g.style.transform = 'translate(0,0)';
      g.style.opacity = '1';
      g.style.filter = '';
    });

    // Reset edges
    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.transition = 'transform 0.7s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease';
      l.style.transform = 'translate(0,0)';
      l.style.opacity = '1';
    });

    // Fade out steps
    stepsLayer.style.opacity = '0';
    setTimeout(() => {
      if (!drilledKind) {
        stepsLayer.innerHTML = '';
        stepsBuilt = false;
        expandedStepIdx = null;
      }
    }, 500);
  }

  function setFocus(nodeId: string): void {
    focusedId = nodeId;
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      const isTarget = g.dataset.nodeId === nodeId;
      g.style.transition = 'opacity 0.4s';
      g.style.opacity = isTarget ? '1' : '0.25';
      g.style.filter = isTarget ? 'drop-shadow(0 0 18px rgba(88,80,200,0.25))' : '';
    });
    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.transition = 'opacity 0.4s';
      l.style.opacity = '0.15';
    });
  }

  function resetFocus(): void {
    focusedId = null;
    overlayLayer.innerHTML = '';
    nodesLayer.querySelectorAll<SVGGElement>('.graph-node').forEach((g) => {
      g.style.opacity = '1';
      g.style.filter = '';
    });
    svg.querySelectorAll<SVGLineElement>('.edges-layer line').forEach((l) => {
      l.style.opacity = '1';
    });
  }

  // ── Espone l'API di selezione "a pezzi" al lesson-builder-service ──
  const selectionApi: MapSelectionApi = {
    enterSelectMode: () => {
      selectMode = true;
      if (drilledKind) styleStepSelection();
      else styleNodeSelection();
    },
    exitSelectMode: () => {
      selectMode = false;
      selectedIds.clear();
      selectedSteps.clear();
      clearSelectionStyles();
      onSelChange?.([]);
    },
    isSelectMode: () => selectMode,
    getSelectedIds: () => currentSelectionIds(),
    clearSelection: () => {
      if (drilledKind) { selectedSteps.clear(); styleStepSelection(); }
      else { selectedIds.clear(); styleNodeSelection(); }
      onSelChange?.([]);
    },
    onSelectionChange: (cb) => { onSelChange = cb; },
    getGraph: () => ({
      kind: 'author-map',
      authorId,
      locale,
      viewBox: [0, 0, VW, VH] as [number, number, number, number],
      nodes: nodes.map((n) => ({
        id: n.id, label: n.label, type: n.type, x: n.x, y: n.y, w: n.w, h: n.h,
        ...(noteFor(n) ? { note: noteFor(n) } : {}),
      })),
      edges: edges.map((e) => ({ from: e.from, to: e.to })),
    }),
    buildCapture,
  };

  // Aggancia l'API all'elemento .author-map-view così il lesson-builder-service
  // (che lavora generico sul DOM) può recuperarla via `__lbMapSelection`.
  const viewEl = container.querySelector<HTMLElement>('.author-map-view');
  if (viewEl) {
    (viewEl as unknown as { __lbMapSelection?: MapSelectionApi }).__lbMapSelection = selectionApi;
  }
}
